import './style.css';
import {
  renderer, scene, camera, yawObject, pitchObject, composer, active,
  renderScale, setRenderScale, scaleCeil, setScaleCeil,
} from './scene.js';
import './world.js';                    // строит карту (наполняет colliders/wallMeshes)
import './input.js';                    // вешает обработчики ввода
import { updateRemotes } from './remote.js';
import { updateOwnBody } from './body.js';
import { updateCorpses } from './corpse.js';
import './combat.js';                   // приём боевых событий с сервера
import { simulateStep, decayPredError, predError, resetPrediction, SIM_DT } from './prediction.js';
import { updateFx } from './fx.js';
import { respawnPlayer, leanShift } from './player.js';
import { guns, tryShoot, updateWeaponView, W } from './weapons.js';
import { updateGrenades, updateNadeAim, throwCurrentNade, exitNade } from './grenades.js';
import { updateAmbients } from './audio.js';
import { updateHUD, drawRadar, debugLine } from './hud.js';
import { player, S, keys, clock } from './state.js';

// ============================================================
//  FPS-прототип: точка входа и игровой цикл.
//  Сервер пока отсутствует — вся логика на клиенте (демо).
// ============================================================

// Симуляция идёт фиксированным шагом 60 Гц (см. prediction.js): локальный
// шаг обязан совпадать с тем, что уходит на сервер. Кадр рендера может быть
// чаще (144 Гц) или реже — аккумулятор это разводит.
let simAcc = 0;
let lastMv = { moving: false, sprint: false, crouch: false, prone: false, landed: false };

function update(dt) {
  // --- смерть/респавн ---
  if (!player.alive) {
    if (S.nadeMode) exitNade();     // граната из руки пропадает при смерти
    // ⚠️ Сбрасываем намерение стрельбы: ЛКМ ставит S.semiQueued/S.firing (input.js),
    // а ранний return ниже пропускает кадровое гашение этих флагов (см. строки ~85/89).
    // Без сброса зависший флаг после респавна даёт фантомный выстрел новым стволом.
    S.firing = false; S.semiQueued = false; S.nadeCharge = 0;
    // ⚠️ И состояние бега: `mv` в этой ветке не считается вовсе, поэтому
    // «прижатое к корпусу» оружие само бы не разжалось — игрок воскрес бы
    // с опущенным стволом и запретом стрелять до первой пробежки.
    S.sprinting = false; S.stow = 0; S.aiming = false;
    // Онлайн воскрешает сервер (см. combat.js), локальный таймер — только офлайн
    if (S.netState !== 'online' && clock.elapsedTime >= player.deadUntil) {
      respawnPlayer(); resetPrediction();
    }
    yawObject.rotation.y = S.yaw; pitchObject.rotation.x = S.pitch;
    updateFx(dt, guns); updateGrenades(dt);
    updateAmbients();               // ⚠️ и мёртвому: дым с огнём слышны с места смерти
    updateRemotes(); updateCorpses(dt);
    updateOwnBody(dt);              // своё тело прячется на время смерти
    simAcc = 0;                     // мёртвый ввод не копим, иначе после респавна рывок
    return;
  }

  // ⚠️ Наклон Q/E считаем ДО симуляции: simulateStep() шлёт его серверу
  // в кадре ввода, и обновлять его после отправки значило бы стабильно
  // отставать на кадр. Тумблеров тут нет — наклон плавно тянется к цели.
  const leanTarget = (keys['KeyE'] ? 1 : 0) - (keys['KeyQ'] ? 1 : 0);
  S.lean += (leanTarget - S.lean) * Math.min(dt * 10, 1);
  // Упор в стену — тем же кодом, что на сервере (player.js::leanShift).
  // Камера обязана стоять там, откуда сервер считает видимость и выстрел.
  S.leanShift = leanShift(player.pos, S.yaw, S.lean);

  // Ограничение шагов за кадр: без него просадка FPS запускает «спираль
  // смерти» — чем больше шагов, тем дольше кадр, тем больше шагов.
  simAcc += dt;
  let steps = 0;
  while (simAcc >= SIM_DT && steps < 5) {
    lastMv = simulateStep(SIM_DT);  // ввод → на сервер + локальное предсказание
    simAcc -= SIM_DT;
    steps++;
  }
  if (steps === 5) simAcc = 0;      // отстали безнадёжно — не догоняем, а сбрасываем
  const mv = lastMv;
  decayPredError(dt);
  // Камера едет с остаточным смещением коррекции: сама позиция игрока уже
  // серверная (стрельба и коллизии — по правде), а картинка догоняет плавно.
  yawObject.position.copy(player.pos).add(predError);

  // --- камера: yaw/pitch + отдача + тряска + покачивание + наклоны ---
  yawObject.rotation.y = S.yaw;
  // ⚠️ Отдачу гасим ДО того, как отдать её камере: tryShoot() ниже по кадру
  // берёт S.pitch + S.recoil для луча, и если камера получит негашёное
  // значение, а выстрел — гашёное, пуля уйдёт ниже перекрестья на 10% отдачи.
  S.recoil *= Math.pow(0.0015, dt);
  pitchObject.rotation.x = S.pitch + S.recoil;
  S.bobT += dt * (mv.sprint ? 14 : 9) * (mv.moving && player.onGround ? 1 : 0);
  const bobY = Math.sin(S.bobT) * 0.035 * (mv.moving ? 1 : 0);
  // наклоны Q/E: смещаем камеру вбок + подкручиваем roll (можно выглянуть из-за угла)
  camera.position.set(
    (Math.random() - 0.5) * S.shake + S.leanShift,
    bobY + (Math.random() - 0.5) * S.shake,
    0);
  camera.rotation.z = Math.cos(S.bobT) * 0.012 * (mv.moving ? 1 : 0) - S.lean * 0.22;
  S.shake *= Math.pow(0.0005, dt);

  updateWeaponView(dt, mv);           // ADS/FOV, прицел, перезарядка, поджим у стены
  if (S.nadeMode) updateNadeAim();    // линия траектории броска

  // --- граната: зажатие ЛКМ копит замах, отпускание бросает ---
  if (S.nadeMode) {
    if (S.firing) { S.nadeCharge = Math.min(1, S.nadeCharge + dt * 1.4); }  // ~0.7 с до полного замаха
    else if (S.nadeCharge > 0) { throwCurrentNade(); }
    S.semiQueued = false;             // чтобы после броска ствол не выстрелил фантомно
  } else if (W().auto) {
    if (S.firing) tryShoot();
  } else if (S.semiQueued) {
    S.semiQueued = false; tryShoot();
  }

  updateFx(dt, guns); updateGrenades(dt);
  // Длящиеся звуки (шипение дымовой, горение молотова) — их громкость и
  // панорама считаются от текущего положения игрока, поэтому каждый кадр.
  updateAmbients();
  updateRemotes();                    // чужие игроки: интерполяция между снапшотами
  updateOwnBody(dt);                  // своё тело: ноги под камерой и своя тень
  updateCorpses(dt);                  // тела убитых: падение, отскоки, растворение

  // --- регенерация HP (только офлайн: в сети её считает сервер) ---
  if (S.netState !== 'online' && clock.elapsedTime - player.lastHurt > 3.5 && player.hp < 100) {
    player.hp = Math.min(100, player.hp + 26 * dt);
  }

  updateHUD(mv.moving);
  drawRadar();
}

// ---- Главный цикл ----
let fpsAcc = 0, fpsFrames = 0, lastScaleUp = 0, lastCeilRelax = 0, badWin = 0;

function loop() {
  requestAnimationFrame(loop);
  const dt = Math.min(clock.getDelta(), 0.05);
  if (active()) update(dt);
  // FPS-счётчик
  fpsAcc += dt; fpsFrames++;
  if (fpsAcc >= 0.5) {
    const fps = fpsFrames / fpsAcc;
    // ---- Динамическое разрешение: держим 60 ----
    //
    // ⚠️ Регулятор ПОМНИТ ПОТОЛОК — тот масштаб, на котором уже не хватило.
    // Без памяти он неизбежно колеблется: понизил → 60 → «есть запас» →
    // повысил → снова 56 → понизил, и так по кругу, а каждый шаг пересоздаёт
    // буферы рендера, то есть подтормаживает. С потолком цикл размыкается:
    // вверх дороги нет, пока потолок не отпустят.
    //
    // ⚠️ Порог 57, а не 60: rAF ограничен вертикальной синхронизацией, выше
    // частоты монитора FPS не поднимется в принципе, и ждать ровно 60.0
    // значило бы валиться вниз до самого пола.
    //
    // ⚠️ Понижаем только после ДВУХ плохих окон подряд. Одно окно — это 0.5 с,
    // и один-единственный пропущенный кадр (взрыв, подгрузка модели) уже даёт
    // 58 вместо 60. Реагировать на такое — значит занижать чёткость из-за
    // случайности.
    if (fps < 57) {
      if (++badWin >= 2) {
        badWin = 0;
        setScaleCeil(renderScale - 0.05);
        setRenderScale(renderScale - 0.05);
      }
    } else {
      badWin = 0;
      if (renderScale < scaleCeil - 0.01 && clock.elapsedTime - lastScaleUp > 3) {
        lastScaleUp = clock.elapsedTime;
        setRenderScale(renderScale + 0.05);
      }
    }
    // Потолок медленно отпускаем: одна тяжёлая сцена не должна держать
    // картинку мягкой до конца матча.
    if (scaleCeil < 1 && clock.elapsedTime - lastCeilRelax > 30) {
      lastCeilRelax = clock.elapsedTime;
      setScaleCeil(scaleCeil + 0.05);
    }
    document.getElementById('fps').textContent = renderScale < 0.99
      ? `${Math.round(fps)} · ${Math.round(renderScale * 100)}%`
      : String(Math.round(fps));
    // Пинг рисуем здесь же, а не каждый кадр: DOM трогать 60 раз в секунду незачем.
    // Рядом с основным показываем пинг второго транспорта — он подключён
    // всё время именно ради этого сравнения (см. net.js). Видно сразу, стоит
    // ли переключаться, и не «показалось» ли.
    const p = document.getElementById('ping');
    const alt = S.transport === 'ws' ? 'rtc' : 'ws';
    const main = S.ping === null ? '—' : Math.round(S.ping);
    const other = S.pingAlt === null ? '—' : Math.round(S.pingAlt);
    p.textContent = `${main} ${S.transport} · ${other} ${alt}`;
    p.className = 'ping ' + (S.ping === null ? '' : S.ping < 60 ? 'good' : S.ping < 130 ? 'mid' : 'bad');
    // Отладочные координаты (F3) — здесь же, дважды в секунду: чаще их всё
    // равно не прочитать глазами, а DOM трогать по 60 раз в секунду незачем.
    document.getElementById('dbg').textContent = S.debugPos ? debugLine() : '';
    fpsAcc = 0; fpsFrames = 0;
  }
  if (S.bloomOn) composer.render(); else renderer.render(scene, camera);
}
loop();

